<template>
  <div
    ref="rootRef"
    :class="bemm('', [isOpen ? 'open' : '', disabled ? 'disabled' : ''])"
    @click.stop
  >
    <div ref="triggerRef" :class="bemm('trigger')" @click.stop="handleTriggerClick">
      <slot name="trigger" :is-open="isOpen" :toggle="toggle" :open="open" :close="close">
        <slot
          name="reference"
          :is-open="isOpen"
          :toggle="toggle"
          :open="open"
          :close="close"
        ></slot>
      </slot>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="panelElRef"
        :class="panelClasses"
        :style="panelStyle"
        @click.stop
      >
        <header v-if="title" :class="bemm('title')">{{ title }}</header>
        <div :class="bemm('content')" @click="closeIfNeeded">
          <slot :is-open="isOpen" :toggle="toggle" :open="open" :close="close"></slot>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useSlots,
  watch,
} from "vue";
import { useBemm } from "bemm";
import type { PopoverPlacement } from "./Popover.model";

defineOptions({
  name: "ArPopover",
});

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    placement?: PopoverPlacement;
    title?: string;
    width?: number | string;
    maxWidth?: number | string;
    trigger?: "click";
    disabled?: boolean;
    closeOnContentClick?: boolean;
  }>(),
  {
    modelValue: undefined,
    placement: "bottom",
    title: "",
    width: undefined,
    maxWidth: undefined,
    trigger: "click",
    disabled: false,
    closeOnContentClick: false,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "change", value: boolean): void;
}>();
const slots = useSlots();

const bemm = useBemm("ar-popover", {
  return: "string",
  includeBaseClass: true,
});

const VIEWPORT_PADDING = 8;
const PANEL_OFFSET = 4;

const internalOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const panelElRef = ref<HTMLElement | null>(null);
const resolvedPlacement = ref<PopoverPlacement>(props.placement);
const panelPosition = ref({ top: 0, left: 0 });

const isControlled = computed(() => typeof props.modelValue === "boolean");
const isOpen = computed(() =>
  isControlled.value ? Boolean(props.modelValue) : internalOpen.value
);

watch(
  () => props.modelValue,
  (value) => {
    if (typeof value === "boolean") {
      internalOpen.value = value;
    }
  }
);

const setOpen = (value: boolean) => {
  if (props.disabled) {
    return;
  }
  if (!isControlled.value) {
    internalOpen.value = value;
  }
  emit("update:modelValue", value);
  emit("change", value);
};

const open = () => setOpen(true);
const close = () => setOpen(false);
const toggle = () => setOpen(!isOpen.value);

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const toCssSize = (value: number | string, fallback: number) => {
  if (typeof value === "number") {
    return `${value}px`;
  }

  const normalized = String(value || fallback).trim();
  return /^\d+(\.\d+)?$/.test(normalized) ? `${normalized}px` : normalized;
};

const updatePanelPosition = () => {
  if (!triggerRef.value) return;

  const triggerRect = triggerRef.value.getBoundingClientRect();
  const initialPlacement = props.placement;

  panelPosition.value = {
    top: triggerRect.bottom + PANEL_OFFSET,
    left: triggerRect.left,
  };
  resolvedPlacement.value = initialPlacement;

  nextTick(() => {
    if (!triggerRef.value || !panelElRef.value) return;

    const rect = triggerRef.value.getBoundingClientRect();
    const panelRect = panelElRef.value.getBoundingClientRect();
    const maxLeft = Math.max(
      VIEWPORT_PADDING,
      window.innerWidth - panelRect.width - VIEWPORT_PADDING
    );
    const maxTop = Math.max(
      VIEWPORT_PADDING,
      window.innerHeight - panelRect.height - VIEWPORT_PADDING
    );

    let nextPlacement = initialPlacement;
    let top = rect.bottom + PANEL_OFFSET;
    let left = rect.left;

    if (initialPlacement === "bottom" || initialPlacement === "top") {
      const canOpenBelow =
        rect.bottom + PANEL_OFFSET + panelRect.height <=
        window.innerHeight - VIEWPORT_PADDING;
      const canOpenAbove = rect.top - PANEL_OFFSET - panelRect.height >= VIEWPORT_PADDING;

      if (initialPlacement === "bottom" && !canOpenBelow && canOpenAbove) {
        nextPlacement = "top";
      }
      if (initialPlacement === "top" && !canOpenAbove && canOpenBelow) {
        nextPlacement = "bottom";
      }

      top =
        nextPlacement === "top"
          ? rect.top - panelRect.height - PANEL_OFFSET
          : rect.bottom + PANEL_OFFSET;
      left = clamp(rect.left, VIEWPORT_PADDING, maxLeft);
    } else {
      const canOpenRight =
        rect.right + PANEL_OFFSET + panelRect.width <=
        window.innerWidth - VIEWPORT_PADDING;
      const canOpenLeft = rect.left - PANEL_OFFSET - panelRect.width >= VIEWPORT_PADDING;

      if (initialPlacement === "right" && !canOpenRight && canOpenLeft) {
        nextPlacement = "left";
      }
      if (initialPlacement === "left" && !canOpenLeft && canOpenRight) {
        nextPlacement = "right";
      }

      left =
        nextPlacement === "left"
          ? rect.left - panelRect.width - PANEL_OFFSET
          : rect.right + PANEL_OFFSET;
      top = clamp(rect.top, VIEWPORT_PADDING, maxTop);
    }

    panelPosition.value = {
      top: clamp(top, VIEWPORT_PADDING, maxTop),
      left: clamp(left, VIEWPORT_PADDING, maxLeft),
    };
    resolvedPlacement.value = nextPlacement;
  });
};

const closeIfNeeded = () => {
  if (props.closeOnContentClick) {
    close();
  }
};

const handleTriggerClick = () => {
  if (slots.trigger) {
    return;
  }
  if (props.trigger === "click") {
    toggle();
  }
};

const onDocumentMouseDown = (event: MouseEvent) => {
  if (!isOpen.value) return;

  const target = event.target as Node;
  const inRoot = rootRef.value?.contains(target);
  const inPanel = panelElRef.value?.contains(target);

  if (!inRoot && !inPanel) {
    close();
  }
};

const onDocumentScroll = (event: Event) => {
  if (!isOpen.value) return;
  if (panelElRef.value?.contains(event.target as Node)) return;
  updatePanelPosition();
};

const onWindowResize = () => {
  if (!isOpen.value) return;
  updatePanelPosition();
};

const panelClasses = computed(() =>
  bemm("panel", [resolvedPlacement.value, isOpen.value ? "open" : ""])
);

const panelStyle = computed(() => {
  const resolvedMaxWidth =
    props.width !== undefined &&
    props.width !== null &&
    props.width !== "" &&
    (props.maxWidth === undefined || props.maxWidth === null || props.maxWidth === "")
      ? props.width
      : props.maxWidth;

  const style: Record<string, string> = {
    position: "fixed",
    top: `${panelPosition.value.top}px`,
    left: `${panelPosition.value.left}px`,
    zIndex: "9999",
    maxWidth: toCssSize(
      resolvedMaxWidth === undefined || resolvedMaxWidth === null
        ? 320
        : resolvedMaxWidth,
      320
    ),
  };

  if (props.width === undefined || props.width === null || props.width === "") {
    return style;
  }

  const width = toCssSize(props.width, 320);
  return {
    ...style,
    width,
  };
});

watch(
  () => isOpen.value,
  (nextOpen) => {
    if (nextOpen) {
      updatePanelPosition();
    }
  }
);

watch(
  () => [props.placement, props.width, props.maxWidth],
  () => {
    if (isOpen.value) {
      updatePanelPosition();
    }
  }
);

onMounted(() => {
  document.addEventListener("mousedown", onDocumentMouseDown);
  document.addEventListener("scroll", onDocumentScroll, true);
  window.addEventListener("resize", onWindowResize);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocumentMouseDown);
  document.removeEventListener("scroll", onDocumentScroll, true);
  window.removeEventListener("resize", onWindowResize);
});
</script>

<style lang="scss">
.ar-popover {
  display: inline-flex;

  &__panel {
    z-index: 30;
    background: var(--color-background);
    border: 1px solid color-mix(in srgb, var(--color-foreground), transparent 86%);
    border-radius: var(--border-radius);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
    padding: var(--space);
    min-width: 180px;
  }

  &__panel--bottom {
  }

  &__panel--top {
  }

  &__panel--left {
  }

  &__panel--right {
  }

  &__title {
    font-size: var(--font-size-s);
    font-weight: var(--font-weight-bold);
    margin-bottom: var(--space-xs);
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);
    font-size: var(--font-size-s);
    line-height: 1.5;
  }

  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}
</style>
